import React, { useState } from "react";
import { Collapse, Divider, List, ListItemButton, ListItemText } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

export interface ITreeListItem {
    id: string;
    title: React.ReactNode | string;
    divider?: boolean;
    children: ITreeListItem[];
}

export const getFirstSelectableTreeListItem = (items: ITreeListItem[], ignoreId: string): ITreeListItem => {
    for (const item of items) {
        if (item.id !== ignoreId && !item.divider && item.children.length === 0) {
            return item;
        }
        const child = getFirstSelectableTreeListItem(item.children, ignoreId);
        if (child) {
            return child;
        }
    }
    return null;
};

interface ITreeListItemProps {
    level: number;
    item: ITreeListItem;
    selectedId: string;
    onSelect: (item: ITreeListItem) => void;
}

export const TreeListItem = (props: ITreeListItemProps) => {
    const [open, setOpen] = useState(props.item?.children?.length > 0);

    const hasChildren = props.item.children.length > 0;

    if (props.item.divider) {
        return <Divider>{props.item.title}</Divider>;
    }

    return (
        <>
            <ListItemButton
                selected={props.selectedId === props.item.id}
                sx={(theme) => (hasChildren
                    ? {
                        "borderBottom": "1px solid",
                        "borderColor": theme.palette.divider,
                        "background": props.level
                            ? "transparent"
                            : theme.palette.action.hover,
                        "maxHeight": props.level
                            ? `max(20px, ${40 - (props.level * 10)}px)`
                            : undefined,
                        "& .MuiListItemText-primary": props.level
                            ? {
                                "fontSize": "14px",
                                "fontStyle": "italic",
                                "fontWeight": "bold",
                            }
                            : undefined,
                    }
                    : null
                )}
                onClick={() => hasChildren ? setOpen(!open) : props.onSelect(props.item)}
            >
                <ListItemText
                    primary={props.item.title}
                />
                {(hasChildren
                    ? (open ? <ExpandLess /> : <ExpandMore />)
                    : null
                )}
            </ListItemButton>
            {hasChildren &&
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {props.item.children.map((child) =>
                            <TreeListItem
                                key={child.id}
                                level={props.level + 1}
                                item={child}
                                selectedId={props.selectedId}
                                onSelect={props.onSelect}
                            />
                        )}
                    </List>
                </Collapse>
            }
        </>
    );
};

interface ITreeListProps {
    items: ITreeListItem[];
    height?: string;
    selectedId: string;
    onSelect: (item: ITreeListItem) => void;
}

export const TreeList = (props: ITreeListProps) => {
    return (
        <List
            component="div"
            disablePadding
            sx={props.height && {
                "overflowY": "auto",
                "maxHeight": props.height,
            }}
        >
            {props.items.map((item) =>
                <TreeListItem
                    key={item.id}
                    level={0}
                    item={item}
                    selectedId={props.selectedId}
                    onSelect={props.onSelect}
                />
            )}
        </List>
    );
};